// ─── i18n — English + Norwegian Bokmål ──────────────────────────────────────
// Language is detected once at module load from the device/browser locale.
// On iOS (Capacitor) navigator.language returns the device language setting.
// Norwegian Bokmål = "nb", Norwegian Nynorsk = "nn", legacy "no".

// Settings can override the auto-detected language by writing to localStorage
// under the key "kickr_lang" (values: "en" | "nb"). This is read once at module
// load — switching languages requires a reload, which the settings screen does.
const storedLang =
  typeof localStorage !== "undefined" ? localStorage.getItem("kickr_lang") : null;

const isNorwegian =
  storedLang === "nb" ||
  (!storedLang &&
    typeof navigator !== "undefined" &&
    (navigator.language?.startsWith("nb") ||
      navigator.language?.startsWith("nn") ||
      navigator.language?.startsWith("no")));

export const lang = isNorwegian ? "nb" : "en";

const EN = {
  // ── Welcome slides ──────────────────────────────────────────────────────────
  slide1Title: "Find a game in Oslo",
  slide1Body: "Browse pickup games by level, format, and time. No club required.",
  slide2Title: "Join in seconds",
  slide2Body: "See who's playing, pick your spot, and show up ready to play.",
  skip: "Skip",
  continue: "Continue",
  setUpProfile: "Set up your profile",

  // ── Profile setup ───────────────────────────────────────────────────────────
  tellOtherPlayers: "Tell other players who you are.",
  onboardingProfile: "Profile",
  onboardingTitle: "Ready to play?",
  onboardingBody: "Add the basics other players need before you join or host.",
  profilePhoto: "Profile photo",
  photoAdded: "Photo added",
  addPhoto: "Add photo",
  changePhoto: "Change photo",
  tapToAddPhoto: "Tap to add photo",
  avatarUploadFailed: "Couldn't upload photo — continuing without one",
  yourName: "Your name",
  username: "Username",
  usernamePlaceholder: "your_handle",
  usernameHelp: "3-20 letters, numbers, or underscores. This is unique.",
  usernameRequired: "Choose a username.",
  usernameInvalid: "Use 3-20 letters, numbers, or underscores.",
  usernameTaken: "That username is already taken.",
  skillLevel: "Skill level",
  position: "Position",
  optionalPosition: "Optional — pick your primary position",
  saving: "Saving...",
  letsPlay: "Let's play",

  // ── Level labels ────────────────────────────────────────────────────────────
  levelBeginner: "Beginner",
  levelIntermediate: "Intermediate",
  levelAdvanced: "Advanced",
  levelAll: "All levels",

  // ── Auth ────────────────────────────────────────────────────────────────────
  welcomeBack: "Welcome back",
  createAccount: "Create account",
  resetPassword: "Reset password",
  emailSent: "Email sent",
  tagline: "Football in Oslo, simplified",
  logIn: "Log in",
  signUp: "Sign up",
  forgotPasswordPrompt: "Enter your email and we'll send a reset link",
  checkInboxPrompt: "Check your inbox and follow the link to set a new password",
  name: "Name",
  email: "Email",
  password: "Password",
  passwordPlaceholder: "Min. 6 characters",
  sendResetLink: "Send reset link",
  sending: "Sending...",
  backToLogIn: "Back to log in",
  forgotPassword: "Forgot password?",
  pleaseWait: "Please wait...",
  continueWithGoogle: "Continue with Google",
  or: "or",
  resetLinkSentTo: "Reset link sent to {email}",
  didntGetIt: "Didn't get it? Check your spam folder.",
  checkEmailConfirm: "Check your email to confirm your account.",
  enterEmailFirst: "Enter your email first",
  noAccount: "No account? ",
  alreadyRegistered: "Already registered? ",
  legalAgreeStart: "I agree to kickr's",
  legalAnd: "and",
  legalAgreement: "Legal agreement",
  legalAgreementHelp: "Required before using kickr in Norway.",
  legalAgreementRequired: "Accept the Terms and Privacy Policy to continue.",
  legalSchemaMissing: "Legal consent tracking is not enabled yet. Run admin/legal_acceptance_migration.sql, then refresh.",
  legalAccepted: "Accepted",
  legalNotAccepted: "Not accepted",

  // ── Game card ───────────────────────────────────────────────────────────────
  today: "TODAY",
  hosting: "Hosting",
  joined: "Joined",
  full: "Full",
  onWaitlist: "On waitlist",
  join: "Join",
  joining: "Joining…",
  joinWaitlist: "Join waitlist",
  joiningWaitlist: "Joining waitlist…",
  leaveGame: "Leave game",
  leaving: "Leaving…",
  leaveWaitlist: "Leave waitlist",
  noBall: "Ball unassigned",
  payAtVenue: "{price} · Pay at venue",
  spotsLeft: "{n} spot left",
  spotsLeftPlural: "{n} spots left",
  needsMorePlayer: "{n} more to start",
  needsMorePlayers: "{n} more to start",
  oneMoreToStart: "1 more to start",
  moreToStart: "{n} more to start",
  oneMorePlayerNeeded: "1 more player needed",
  morePlayersNeeded: "{n} more players needed",
  waitingForPlayers: "Waiting for players",
  gameConfirmed: "Confirmed",
  confirmWhenEnoughPlayers: "Runs when {n} players have joined. Auto-cancels and notifies players 3h before kickoff if it stays below minimum.",
  autoConfirmsAt: "Runs once {n} players have joined.",
  completed: "Completed",
  timeTbc: "Time TBC",
  venueTbc: "Venue TBC",
  saveGame: "Save game",
  removeFromSaved: "Remove from saved",
  private: "Private",

  // ── Game sheet tabs ─────────────────────────────────────────────────────────
  tabInfo: "Info",
  tabPlayers: "Players",
  tabChat: "Chat",

  // ── Game sheet — info ───────────────────────────────────────────────────────
  teams: "Teams",
  teamA: "Team A",
  teamB: "Team B",
  unassigned: "Unassigned",
  dispatchRandom: "Random fair",
  dispatchRandomDesc: "Balanced by skill — alternates from highest to lowest rated players",
  dispatchManual: "Manual",
  dispatchManualDesc: "Tap players to assign them to Team A or Team B yourself",
  scanToJoin: "Scan to join this game",
  pointsTo: "Points to: {url}",
  duration: "Duration",
  cost: "Cost",
  format: "Format",
  minAge: "Min age",
  venue: "Venue",
  getDirections: "Open address",
  host: "Host",
  you: "you",
  addToCalendar: "Add to calendar",
  calendarOpened: "Calendar opened",
  calendarFileDownloaded: "Calendar file downloaded",
  couldNotOpenCalendar: "Could not open calendar",
  willCancel: "Will cancel automatically if minimum isn't reached 3h before kickoff",
  needsMoreToConfirm1: "1 more player needed",
  needsMoreToConfirmN: "{n} more players needed",
  costPerPlayer: "Cost: {price} per player",
  paymentAtVenue: "Payment is collected at the venue. Bring cash or Vipps.",
  playerOnWaitlist: "{n} player on waitlist",
  playersOnWaitlist: "{n} players on waitlist",
  chanceOfRainBad: "{rain}% chance of rain — bring a jacket!",
  chanceOfRainGood: "{rain}% chance of rain — good conditions",
  nobodyHasTheBall: "Nobody has the ball!",
  ballLabel: "Ball",
  nobodyYet: "Nobody yet",
  youreBreingIt: "You're bringing it",
  illBringIt: "I'll bring it",
  drop: "Drop",
  reassign: "Reassign",
  whyCancelling: "Why are you cancelling?",
  reasonVenue: "Venue not available",
  reasonPlayers: "Not enough players",
  reasonWeather: "Weather",
  reasonPersonal: "Personal reason",
  editGame: "Edit game",
  cancelGame: "Cancel game",
  saveChanges: "Save changes",
  cancel: "Cancel",
  back: "Back",
  close: "Close",
  save: "Save",
  dispatchTeams: "Dispatch Teams",
  splitPlayers: "Split {n} players into Team A and Team B.",
  confirmCancel: "Confirm cancel",
  clearTeams: "Clear teams",
  cloneForNextWeek: "Clone for next week",
  gameClonedForNextWeek: "Cloned for next week",
  publishing: "Publishing…",
  confirmAndPublish: "Confirm & Publish",
  openSpot: "Open spot",
  waitlistHeader: "Waitlist · {n}",
  waiting: "Waiting",
  promote: "Promote",
  remove: "Remove",
  yourGame: "You're hosting",

  // ── Chat ────────────────────────────────────────────────────────────────────
  chat: "Chat",
  joinToAccessChat: "Join to access chat",
  chatOnlyForPlayers: "Chat is only available to players in this game",
  noMessagesYet: "No messages yet. Say hello!",
  loading: "Loading...",
  messageGroup: "Message the group",
  directChatSubtitle: "Friend chat",
  directChatPlaceholder: "Message...",
  directChatEmpty: "No messages yet. Start with a quick hello.",
  directChatOffline: "Messages are temporarily unavailable.",
  send: "Send",
  joinGame: "Join game",
  chatMsg1: "On my way",
  chatMsg2: "Running ~10min late",
  chatMsg3: "I'm here!",
  chatMsg4: "Who has the ball?",

  // ── Notifications ───────────────────────────────────────────────────────────
  notifications: "Notifications",
  markAllRead: "Mark all read",
  clearAll: "Clear all",
  accept: "Accept",
  decline: "Decline",

  // ── Friends ─────────────────────────────────────────────────────────────────
  friends: "Friends",
  findPlayers: "Find players",
  searchByName: "Search by name…",
  requests: "Requests · {n}",
  yourFriends: "Your friends · {n}",
  addMeOnKickr: "Add me on kickr",
  scanToSendRequest: "Scan to send a friend request",
  copyLink: "Copy link",
  add: "Add",
  friends_label: "Friends",
  requested: "Requested",
  invite: "Invite",
  noFriendsYet: "No friends yet — search for players above!",
  noOpenGames: "No open games to invite to yet.",
  alreadyInGames: "{firstName}'s already in all your open games.",
  createAGame: "Create a game",

  // ── Create / Edit game ──────────────────────────────────────────────────────
  createGame: "Create game",
  createAGameTitle: "Create a game",
  titleLabel: "Title",
  titlePlaceholder: "e.g. Wednesday 5v5",
  descriptionLabel: "Description",
  descriptionPlaceholder: "Any details for players...",
  dateLabel: "Date",
  timeLabel: "Time",
  kickoffTime: "Kickoff time",
  durationLabel: "Duration — {minutes} min",
  minPlayersLabel: "Minimum players to confirm game",
  minPlayersHint: "Game auto-cancels and notifies players if minimum isn't reached 3 hours before kickoff",
  priceLabel: "Price",
  costPerPlayerLabel: "Cost per player",
  repeatLabel: "Repeat weekly",
  repeatDescription: "Create the same game for multiple weeks",
  repeatForHowManyWeeks: "Repeat for how many weeks?",
  weeksLabel: "{n} weeks",
  searchVenues: "Search venues…",
  customLocation: "+ Custom location",
  address: "Address",
  venueLabel: "Venue",
  stepFormat: "Format",
  stepVenue: "Venue",
  stepTime: "Time",
  stepLabel: "Step {current} of {total}",
  whatKindOfGame: "What kind of game?",
  whereAndWho: "Where and who?",
  whenIsIt: "When is it?",
  creating: "Creating...",
  summaryLabel: "Summary",
  formatSummary: "Format",
  timeSummary: "Time",
  durationSummary: "Duration",
  costSummary: "Cost",
  minPlayersSummary: "Min players to confirm",
  maxPlayersSummary: "Max players",
  paymentCollected: "Payment is collected at the venue. Players are expected to pay {price} when they arrive.",
  gameAlreadyHere: "Game already here",
  gameAlreadyHereDesc: "There {is_are} already {n} game{s} at this venue on {date}. Want to join instead?",
  createAnyway: "Create anyway",
  view: "View",
  cloning: "Cloning last game — edit anything below",
  thisTimePassed: "This time has already passed — please pick a future time",
  addressSuggestion: "Select an address from the suggestions",
  repeatWeeksWillCreate: "{n} game will be created, starting {date}",
  repeatWeeksWillCreatePlural: "{n} games will be created, starting {date}",
  ageRestriction: "Age restriction",
  ageRestrictionHint: "Only show to players {age}+",
  privateGame: "Private game",
  privateGameHint: "Only invited players can see this",

  // ── Discover / Filters ──────────────────────────────────────────────────────
  discoverTab: "Discover",
  myGamesTab: "My Games",
  mapTab: "Map",
  profileTab: "Profile",
  communitiesTab: "Social",
  searchPlaceholder: "Search games, hosts...",
  filters: "Filters",
  clearFilters: "Clear filters",
  level: "Level",
  dateFilter: "Date",
  distanceFilter: "Distance",
  sortBy: "Sort by",
  anyDate: "Any date",
  dateFrom: "From",
  dateUntil: "Until",
  dateRangeHint: "Pick dates or use a shortcut",
  next7Days: "Next 7 days",
  tomorrow: "Tomorrow",
  weekend: "Weekend",
  anyDistance: "Any distance",
  withinKm: "Within {n} km",
  distanceRadius: "Radius",
  distanceRangeHint: "Drag to limit games near you",
  useLocationForRadius: "Enable location to use radius",
  availabilityFilter: "Availability",
  showFullyBooked: "Show fully booked",
  showFullyBookedHint: "Include games with no spots left",
  bestMatch: "Best match",
  nearestFirst: "Nearest first",
  soonestFirst: "Soonest first",
  showGames: "Show {n} game",
  showGamesPlural: "Show {n} games",
  playingToday: "Playing today — spots available",
  createGameHere: "Create game here",
  createNewGameHere: "+ Create a new game here",
  activeGames: "Active games · {n}",
  signInToJoin: "Sign in",

  // ── My Games ─────────────────────────────────────────────────────────────────
  searchMyGames: "Search by title or location...",
  upcoming: "Upcoming",
  past: "Past",
  saved: "Saved",
  noUpcomingGames: "You haven't joined any games yet",
  findAGame: "Find a game",
  noGamesMatch: 'No games match "{q}"',
  clearSearch: "Clear search",
  noPastGames: "No past games yet",
  noSavedGames: "No saved games yet",
  browseGames: "Browse games",
  showingCompleted: "Showing {n} completed game",
  showingCompletedPlural: "Showing {n} completed games",
  yourGamesHere: "Your games live here",
  yourGamesHereDesc: "Sign in to join games, track your schedule, and connect with other players.",

  // ── Profile ──────────────────────────────────────────────────────────────────
  profileTab2: "Profile",
  yourProfile: "Your profile",
  yourProfileDesc: "Sign in to build your profile, set your position, and track your football history.",
  age: "Age",
  bio: "Bio",
  gamesPlayed: "Games",
  hostedGames: "Hosted",
  signOut: "Sign out",
  editProfile: "Edit",
  cancelEdit: "Cancel",
  saveProfile: "Save",
  profileSaved: "Profile saved",
  deleteAccount: "Delete account",
  deleteAccountConfirm: "Are you sure? This will permanently delete your account and all your data.",
  deleting: "Deleting…",
  yesDelete: "Yes, delete",
  reliabilityScore: "Reliability",

  // ── Availability ─────────────────────────────────────────────────────────────
  imAvailable: "I'm Available",
  imAvailableHint: "Tell nearby players when you're free",
  setAvailability: "Set Availability",
  availabilityDesc: "Set when you're free. If {n} or more players are available near you at the same time, everyone gets notified and you can create a game in one tap.",
  from: "From",
  to: "To",
  availabilityTime: "Time",
  availabilityTimeHint: "Drag both ends to set when you're free",
  allDay: "All day",
  allDayHint: "You'll appear available for the full selected day.",
  formatOptional: "Format (optional)",
  levelOptional: "Level (optional)",
  leaveBlankAnyFormat: "Leave blank to match any format.",
  leaveBlankAnyLevel: "Leave blank to match any level.",
  clear: "Clear",
  freeToday: "Free today",
  freePlayers: "{n} player also free nearby",
  freePlayersPlural: "{n} players also free nearby",
  playersFreeNearbyShort: "{n} free nearby",
  availabilityMomentumHint: "Add yours to get a game going.",
  joinNearbyPlayers: "Join them",
  playersFreeCta: "{n} players free — create a game?",
  edit: "Edit",

  // Communities
  communitiesTitle: "Groups",
  communitiesSubtitle: "Join groups of players in your city",
  yourCommunities: "Your groups",
  discoverCommunities: "Discover",
  createCommunity: "Create group",
  communityName: "Group name",
  communityDescription: "Description",
  communityPrivacy: "Privacy",
  communityPublic: "Public",
  communityPrivate: "Private",
  communityPublicDesc: "Anyone can find and join",
  communityPrivateDesc: "Invite-only, hidden from Discover",
  communityJoinPolicy: "Who can join",
  joinPolicyOpen: "Anyone",
  joinPolicyRequest: "By request",
  joinPolicyInviteOnly: "Invite only",
  communityCity: "City",
  communityTags: "Tags",
  noCommunitiesYet: "No groups yet",
  communitiesComingSoon: "Create one or join an existing group to play regularly with the same people.",
  noCommunitiesInCity: "No groups in your city yet",
  beFirstToCreate: "Be the first to create one.",
  joinCommunity: "Join",
  leaveCommunity: "Leave",
  requestToJoin: "Request to join",
  requestPending: "Request pending",
  membersCount: "{n} members",
  memberSingular: "1 member",
  feedTab: "Feed",
  gamesTab: "Games",
  membersTab: "Members",
  ownerRole: "Owner",
  adminRole: "Admin",
  modRole: "Mod",
  memberRole: "Member",
  communityGamesEmpty: "No games in this group yet",
  communityCreate: "Create",
  creatingCommunity: "Creating…",
  communityCreateError: "Could not create group",
  communityJoinError: "Could not join",
  communityLeaveError: "Could not leave",
  confirmLeaveCommunity: "Leave this group?",
  gameScope: "Scope",
  scopePublic: "Public",
  scopeCommunity: "Group",
  scopePrivate: "Private",
  scopePublicDesc: "Open to everyone",
  scopeCommunityDesc: "Only visible to group members",
  scopePrivateDesc: "Invite-only",
  pickCommunity: "Pick a group",
  featuredCommunity: "Featured group",
  memberBadge: "Member",
  communitiesHeroTitle: "Find your team in {city}",
  communitiesHeroSubtitle: "Join a group of players who meet up regularly. New friends, new games, every week.",
  trendingIn: "Trending in {city}",
  moreToExplore: "More to explore",

  // ── Communities Discover (redesigned) ────────────────────────────────────────
  communities: "Groups",
  myCommunities: "My Groups",
  allCommunities: "All Groups",
  all: "All",
  mine: "Mine",
  discover: "Discover",
  searchCommunitiesPlaceholder: "Search groups, tags, locations…",
  trendingToday: "Trending Today",
  trendingPopular: "Most Popular",
  trendingActive: "Most Active",
  trendingNewest: "Newest",
  filterLocation: "Location",
  filterTags: "Tags",
  filterSort: "Sort",
  filterLocationTitle: "Filter by location",
  filterTagsTitle: "Filter by tag",
  filterSortTitle: "Sort by",
  anyLocation: "Anywhere",
  anyTag: "Any tag",
  sortByPopular: "Most popular",
  sortByActive: "Most active",
  sortByNewest: "Newest",
  noCity: "No location",
  viewCommunity: "View group",
  openCommunity: "Open",
  verifiedBadge: "Verified",
  communityOpen: "Open",
  communityClosed: "Closed",
  noCommunitiesMatch: "No groups match those filters",
  noJoinedCommunities: "You haven't joined any groups yet",
  showGroups: "Show groups",

  // ── Community Screen (redesigned) ────────────────────────────────────────────
  aboutTab: "About",
  aboutDescriptionTitle: "Description",
  aboutNoDescription: "This group hasn't shared a description yet.",
  communityTagsTitle: "Group tags",
  verifiedCommunityTitle: "Verified group",
  verifiedCommunityDesc: "This group has been reviewed and trusted by the kickr team.",
  communityAdminTitle: "Group admin",
  statMembersLabel: "Members",
  statGamesLabel: "Games",
  shareCommunity: "Share",
  reportCommunity: "Report",
  reportCommunityTitle: "Report this group",
  reportCommunityHint: "Tell us what's wrong. A moderator will review your report.",
  reportReasonPlaceholder: "Describe the problem (spam, abuse, off-topic…)",
  submitReport: "Submit report",
  reporting: "Submitting…",
  reportSubmitted: "Report submitted — thanks for letting us know.",
  reportError: "Could not submit report",
  youSuffix: "you",
  editCover: "Edit cover",
  editLogo: "Edit logo",
  uploading: "Uploading…",
  imageUploadError: "Could not upload image",
  imageBucketMissing: "Storage not set up — admin must create a \"community-images\" bucket.",
  postPlaceholder: "Share something with the group…",
  post: "Post",
  posting: "Posting…",
  postEmptyError: "Write something first",
  postError: "Could not publish post",
  noPostsYet: "Nothing posted yet",
  beTheFirst: "Be the first to share something with this group.",
  joinToPost: "Join the group to post.",
  pinned: "Pinned",
  pinPost: "Pin",
  unpinPost: "Unpin",
  deletePost: "Delete",
  confirmDeletePost: "Delete this post?",
  edited: "edited",
  justNow: "just now",
  minutesAgo: "{n}m ago",
  hoursAgo: "{n}h ago",
  daysAgo: "{n}d ago",
  createGameInCommunity: "Create a game for this group",
  shareableGamesTitle: "Share one of your games",
  shareableGamesHint: "Add an existing game you host so members can see it here.",
  shareToCommunity: "Add",
  sharingGame: "Adding…",
  chatTab: "Chat",
  chatPlaceholder: "Message the group…",
  chatJoinToWrite: "Join the group to chat.",
  chatEmpty: "No messages yet. Say hi!",
  chatSendError: "Could not send message",
  challengesTitle: "Challenges",
  challengeAnotherCommunity: "Challenge another group",
  pickOpponent: "Pick a group to challenge",
  proposeMatch: "Propose match",
  sendingChallenge: "Sending…",
  challengeSent: "Challenge sent!",
  challengeAccept: "Accept",
  challengeDecline: "Decline",
  challengeCancel: "Cancel",
  challengePendingIn: "Pending challenges to your group",
  challengePendingOut: "Pending challenges you sent",
  challengeAcceptedTitle: "Accepted matches",
  challengeAcceptedHint: "An admin can now create the game.",
  noOpponentsAvailable: "No other groups available yet.",
  challengeNoteOptional: "Add a note (optional)",
  challengeError: "Could not send challenge",
  challengeUpdateError: "Could not update challenge",
  vsLabel: "vs",
  challengeNotAdmin: "Only admins can challenge other groups.",
  locationLabel: "Location",

  // ── Referee (optional) ───────────────────────────────────────────────────────
  refereeLabel: "Referee",
  refereeOptional: "Referee (optional)",
  refereeHelp: "Pick a friend to ref this game",
  pickReferee: "Pick a referee",
  searchFriends: "Search friends",
  noFriendsForReferee: "Add some friends first to assign a referee",
  noFriendsMatch: "No friends match",
  clearReferee: "Clear",
  refereeBadge: "Ref",
  notifRefereeAssignedText: "You're refereeing \"{title}\"",
  notifRefereeAssignedSub: "{date} · {time}",
  noneLabel: "None",
  createGameFromChallenge: "Create the game",

  // ── Toast messages ───────────────────────────────────────────────────────────
  linkCopied: "Link copied!",
  couldNotCopyLink: "Could not copy link",
  teamsCleared: "Teams cleared",
  failedToClearTeams: "Failed to clear teams",
  gameUpdated: "Game updated!",
  failedToSave: "Failed to save — try again",
  teamsPublished: "Teams published!",
  couldNotSaveTeams: "Could not save teams",
  failedToSendMessage: "Failed to send message — try again",
  failedToRemovePlayer: "Failed to remove player",
  friendRequestSent: "Friend request sent!",
  couldNotSendRequest: "Could not send request",
  friendAdded: "Friend added!",
  couldNotAcceptRequest: "Could not accept request",
  couldNotDeclineRequest: "Could not decline request",
  couldNotSendInvite: "Could not send invite — try again",
  availabilitySaveError: "Could not save availability. Try again in a moment.",
  backupPitchSyncWarning: "Game created, but the backup pitch could not sync for everyone yet.",
  errorCancellingGame: "Error cancelling game",
  youJoined: "You joined the game!",
  leftGame: "Left the game",
  leftGameWaitlistPromoted: "Left the game. {name} moved off the waitlist.",
  addedToWaitlist: "Added to waitlist — you'll be notified if a spot opens",
  removedFromWaitlist: "Removed from waitlist",
  somethingWentWrong: "Something went wrong",
  couldNotGenerateQR: "Could not generate QR",
  gameCreated: "Game created",
  gamesCreatedSeries: "{n} games created (weekly series)",
  gameCancelledNotified: "Game cancelled — {n} player{s} notified",
  gameCancelled: "Game cancelled",
  requestNoLongerAvailable: "Request no longer available",
  inviteSent: "Invite sent for {title}!",
  failedToLoadGames: "Failed to load games",
  reloadApp: "Reload app",
  somethingWrongErrorBoundary: "Something went wrong",
  playerRemovedFromGame: "{name} removed from game",
  playerPromotedFromWaitlist: "{name} promoted from waitlist",
  failedToPromotePlayer: "Failed to promote player",
  mustBeSignedIn: "You must be signed in to create a game",
  anyFormat: "Any format",
  memberSince: "Member since",
  joinedStat: "Joined",
  noBioYet: "No bio yet",
  basedOnGames: "Based on {n} game{s}",
  deleteAccountTitle: "Delete your account?",
  deleteAccountBody: "This will permanently remove your profile and game history. This cannot be undone.",

  // ── Countdown strings ────────────────────────────────────────────────────────
  startingNow: "Starting now",
  startedMAgo: "Started {m}m ago",
  startedHAgo: "Started {h}h ago",
  startedDAgo: "Started {d}d ago",
  startsInM: "Starts in {m}m",
  startsInHM: "Starts in {h}h {m}m",
  startsInDay: "Starts in {d} day",
  startsInDays: "Starts in {d} days",

  // ── Date labels ──────────────────────────────────────────────────────────────
  todayLabel: "Today",
  tomorrowLabel: "Tomorrow",

  // ── Venue page (TGTG-style) ──────────────────────────────────────────────────
  overallExperience: "Overall pitch experience",
  basedOnReviews: "Based on {n} review{s}",
  noReviewsYet: "No reviews yet — be the first to leave one",
  surface: "Surface",
  availability: "Availability",
  lighting: "Lighting",
  directions: "Directions",
  upcomingHere: "Upcoming games here",
  noUpcomingHere: "No upcoming games at this venue",
  nearbyAlternatives: "Nearby alternatives",

  // ── Post-game review (simplified) ────────────────────────────────────────────
  gameFinished: "Game finished",
  latestGameReady: "{title} is ready to wrap up.",
  wrapUpLatest: "Wrap up your latest game",
  wrapUp: "Wrap up",
  finishReport: "Finish report",
  reviewed: "Reviewed",
  matchReportTitle: "Wrap up this game",
  matchReportHint: "Takes a few seconds after the game.",
  rateThisGame: "How was this game?",
  manOfTheMatch: "Man of the Match",
  manOfTheMatchHint: "Pick the player who stood out.",
  optionalNote: "Optional note (private)",
  optionalNotePlaceholder: "Anything to remember?",
  attendance: "Attendance",
  noShowQuestion: "Who missed the game?",
  noShowCaution: "Only mark players who were expected but did not arrive.",
  everyoneShowed: "Everyone showed",
  selected: "Selected",
  markNoShow: "Mark no-show",
  reportedNoShow: "No-show",
  reportNoShows: "Report no-shows",
  noShowsHelp: "Tap a player who didn't show. They keep a fair reliability score.",
  noPlayersToReport: "No other players were listed for this game.",
  ratePitchOptional: "Rate the pitch (optional)",
  ratePitchHelp: "Helps the next person picking this venue.",
  pitchFree: "Free",
  pitchTaken: "Taken",
  submitReview: "Submit review",
  everythingGood: "Everything was fine",
  reviewThanks: "Thanks for the feedback",
  alreadyReviewed: "You've reviewed this game",
  later: "Later",

  // ── Profile (refined) ────────────────────────────────────────────────────────
  memberSinceShort: "Member since {d}",
  recentActivity: "Recent activity",
  seeAllPast: "See all past games",
  achievements: "Achievements",
  noAchievementsYet: "Play your first game to earn your first badge",
  avgRatingReceived: "Avg rating",
  avgRatingNone: "No ratings yet",
  badgePunctual: "Punctual",
  badgePunctualDesc: "Zero no-shows in 5+ games",
  badgeHost: "Host",
  badgeHostDesc: "Hosted 1+ game",
  badgeRegular: "Regular",
  badgeRegularDesc: "10+ games played",
  badgeVeteran: "Veteran",
  badgeVeteranDesc: "50+ games played",
  badgeFiveStar: "Top-rated",
  badgeFiveStarDesc: "Avg rating ≥ 4.5",

  // ── Settings screen ──────────────────────────────────────────────────────────
  settings: "Settings",
  account: "Account",
  preferences: "Preferences",
  privacy: "Privacy",
  notificationsGameReminders: "Game reminders",
  notificationsFriendRequests: "Friend requests",
  notificationsChat: "Chat messages",
  language: "Language",
  theme: "Theme",
  themeLight: "Light",
  themeDark: "Dark",
  themeSystem: "System",
  profileVisibility: "Profile visibility",
  profileVisibilityPublic: "Public",
  profileVisibilityFriends: "Friends only",
  about: "About",
  appVersion: "App version",
  termsOfService: "Terms of service",
  privacyPolicy: "Privacy policy",
  greetingMorning: "Good morning",
  greetingAfternoon: "Good afternoon",
  greetingEvening: "Good evening",
  greetingNight: "Playing late",
  games: "Games",
  venues: "Venues",
  players: "Players",
};

const NB = {
  // ── Welcome slides ──────────────────────────────────────────────────────────
  slide1Title: "Finn en kamp i Oslo",
  slide1Body: "Bla gjennom kamper etter nivå, format og tid. Ingen klubb nødvendig.",
  slide2Title: "Bli med på sekunder",
  slide2Body: "Se hvem som spiller, velg plassen din, og møt opp klar til å spille.",
  skip: "Hopp over",
  continue: "Fortsett",
  setUpProfile: "Sett opp profilen din",

  // ── Profile setup ───────────────────────────────────────────────────────────
  tellOtherPlayers: "Fortell andre spillere hvem du er.",
  onboardingProfile: "Profil",
  onboardingTitle: "Klar for å spille?",
  onboardingBody: "Legg til det viktigste andre spillere trenger før du blir med eller arrangerer.",
  profilePhoto: "Profilbilde",
  photoAdded: "Bilde lagt til",
  addPhoto: "Legg til bilde",
  changePhoto: "Endre bilde",
  tapToAddPhoto: "Trykk for å legge til bilde",
  avatarUploadFailed: "Kunne ikke laste opp bilde — fortsetter uten",
  yourName: "Ditt navn",
  username: "Brukernavn",
  usernamePlaceholder: "ditt_navn",
  usernameHelp: "3-20 bokstaver, tall eller understrek. Dette er unikt.",
  usernameRequired: "Velg et brukernavn.",
  usernameInvalid: "Bruk 3-20 bokstaver, tall eller understrek.",
  usernameTaken: "Dette brukernavnet er allerede tatt.",
  skillLevel: "Ferdighetsnivå",
  position: "Posisjon",
  optionalPosition: "Valgfritt — velg din primærposisjon",
  saving: "Lagrer...",
  letsPlay: "La oss spille",

  // ── Level labels ────────────────────────────────────────────────────────────
  levelBeginner: "Nybegynner",
  levelIntermediate: "Middels",
  levelAdvanced: "Avansert",
  levelAll: "Alle nivåer",

  // ── Auth ────────────────────────────────────────────────────────────────────
  welcomeBack: "Velkommen tilbake",
  createAccount: "Opprett konto",
  resetPassword: "Tilbakestill passord",
  emailSent: "E-post sendt",
  tagline: "Fotball i Oslo, forenklet",
  logIn: "Logg inn",
  signUp: "Registrer deg",
  forgotPasswordPrompt: "Skriv inn e-posten din, så sender vi en tilbakestillingslenke",
  checkInboxPrompt: "Sjekk innboksen din og følg lenken for å sette et nytt passord",
  name: "Navn",
  email: "E-post",
  password: "Passord",
  passwordPlaceholder: "Min. 6 tegn",
  sendResetLink: "Send tilbakestillingslenke",
  sending: "Sender...",
  backToLogIn: "Tilbake til innlogging",
  forgotPassword: "Glemt passordet?",
  pleaseWait: "Vennligst vent...",
  continueWithGoogle: "Fortsett med Google",
  or: "eller",
  resetLinkSentTo: "Tilbakestillingslenke sendt til {email}",
  didntGetIt: "Fikk du den ikke? Sjekk søppelpostmappen.",
  checkEmailConfirm: "Sjekk e-posten din for å bekrefte kontoen din.",
  enterEmailFirst: "Skriv inn e-posten din først",
  noAccount: "Ingen konto? ",
  alreadyRegistered: "Allerede registrert? ",
  legalAgreeStart: "Jeg godtar kickrs",
  legalAnd: "og",
  legalAgreement: "Juridisk samtykke",
  legalAgreementHelp: "Påkrevd før du bruker kickr i Norge.",
  legalAgreementRequired: "Godta vilkårene og personvernerklæringen for å fortsette.",
  legalSchemaMissing: "Sporing av juridisk samtykke er ikke aktivert ennå. Kjør admin/legal_acceptance_migration.sql, og oppdater.",
  legalAccepted: "Godtatt",
  legalNotAccepted: "Ikke godtatt",

  // ── Game card ───────────────────────────────────────────────────────────────
  today: "I DAG",
  hosting: "Vert",
  joined: "Påmeldt",
  full: "Full",
  onWaitlist: "På venteliste",
  join: "Bli med",
  joining: "Melder på…",
  joinWaitlist: "Bli med på venteliste",
  joiningWaitlist: "Legger til på venteliste…",
  leaveGame: "Forlat kamp",
  leaving: "Forlater…",
  leaveWaitlist: "Forlat venteliste",
  noBall: "Ball ikke avklart",
  payAtVenue: "{price} · Betal på banen",
  spotsLeft: "{n} plass igjen",
  spotsLeftPlural: "{n} plasser igjen",
  needsMorePlayer: "{n} til for start",
  needsMorePlayers: "{n} til for start",
  oneMoreToStart: "1 til for start",
  moreToStart: "{n} til for start",
  oneMorePlayerNeeded: "1 spiller til trengs",
  morePlayersNeeded: "{n} spillere til trengs",
  waitingForPlayers: "Venter på spillere",
  gameConfirmed: "Bekreftet",
  confirmWhenEnoughPlayers: "Spilles når {n} spillere har blitt med. Avlyses automatisk og varsler spillere 3 t før avspark hvis minimum ikke er nådd.",
  autoConfirmsAt: "Spilles når {n} spillere har blitt med.",
  completed: "Ferdig",
  timeTbc: "Tid kommer",
  venueTbc: "Bane kommer",
  saveGame: "Lagre kamp",
  removeFromSaved: "Fjern fra lagrede",
  private: "Privat",

  // ── Game sheet tabs ─────────────────────────────────────────────────────────
  tabInfo: "Info",
  tabPlayers: "Spillere",
  tabChat: "Chat",

  // ── Game sheet — info ───────────────────────────────────────────────────────
  teams: "Lag",
  teamA: "Lag A",
  teamB: "Lag B",
  unassigned: "Ikke tildelt",
  dispatchRandom: "Tilfeldig rettferdig",
  dispatchRandomDesc: "Balansert etter ferdighet — alternerer fra høyest til lavest rangerte spillere",
  dispatchManual: "Manuell",
  dispatchManualDesc: "Trykk på spillere for å tildele dem til Lag A eller Lag B selv",
  scanToJoin: "Skann for å bli med i denne kampen",
  pointsTo: "Peker til: {url}",
  duration: "Varighet",
  cost: "Kostnad",
  format: "Format",
  minAge: "Minalder",
  venue: "Bane",
  getDirections: "Åpne adresse",
  host: "Vert",
  you: "deg",
  addToCalendar: "Legg til i kalender",
  calendarOpened: "Kalender åpnet",
  calendarFileDownloaded: "Kalenderfil lastet ned",
  couldNotOpenCalendar: "Kunne ikke åpne kalender",
  willCancel: "Avlyses automatisk hvis minimum ikke er nådd 3 timer før avspark",
  needsMoreToConfirm1: "1 spiller til trengs",
  needsMoreToConfirmN: "{n} spillere til trengs",
  costPerPlayer: "Kostnad: {price} per spiller",
  paymentAtVenue: "Betaling skjer på banen. Ta med kontanter eller Vipps.",
  playerOnWaitlist: "{n} spiller på venteliste",
  playersOnWaitlist: "{n} spillere på venteliste",
  chanceOfRainBad: "{rain}% sjanse for regn — ta med jakke!",
  chanceOfRainGood: "{rain}% sjanse for regn — gode forhold",
  nobodyHasTheBall: "Ingen har ballen!",
  ballLabel: "Ball",
  nobodyYet: "Ingen ennå",
  youreBreingIt: "Du tar med ballen",
  illBringIt: "Jeg tar med den",
  drop: "Slipp",
  reassign: "Tildel på nytt",
  whyCancelling: "Hvorfor avlyser du?",
  reasonVenue: "Bane ikke tilgjengelig",
  reasonPlayers: "Ikke nok spillere",
  reasonWeather: "Vær",
  reasonPersonal: "Personlig grunn",
  editGame: "Rediger kamp",
  cancelGame: "Avlys kamp",
  saveChanges: "Lagre endringer",
  cancel: "Avbryt",
  back: "Tilbake",
  close: "Lukk",
  save: "Lagre",
  dispatchTeams: "Send ut lag",
  splitPlayers: "Del {n} spillere i Lag A og Lag B.",
  confirmCancel: "Bekreft avlysning",
  clearTeams: "Tøm lag",
  cloneForNextWeek: "Klon for neste uke",
  gameClonedForNextWeek: "Klonet for neste uke",
  publishing: "Publiserer…",
  confirmAndPublish: "Bekreft og publiser",
  openSpot: "Ledig plass",
  waitlistHeader: "Venteliste · {n}",
  waiting: "Venter",
  promote: "Forfrem",
  remove: "Fjern",
  yourGame: "Du er vert",

  // ── Chat ────────────────────────────────────────────────────────────────────
  chat: "Chat",
  joinToAccessChat: "Bli med for å bruke chat",
  chatOnlyForPlayers: "Chat er kun tilgjengelig for spillere i dette spillet",
  noMessagesYet: "Ingen meldinger ennå. Si hei!",
  loading: "Laster...",
  messageGroup: "Send melding til gruppen",
  directChatSubtitle: "Vennechat",
  directChatPlaceholder: "Melding...",
  directChatEmpty: "Ingen meldinger ennå. Start med et hei.",
  directChatOffline: "Meldinger er midlertidig utilgjengelige.",
  send: "Send",
  joinGame: "Bli med",
  chatMsg1: "På vei",
  chatMsg2: "Rundt 10 min forsinket",
  chatMsg3: "Jeg er her!",
  chatMsg4: "Hvem har ballen?",

  // ── Notifications ───────────────────────────────────────────────────────────
  notifications: "Varsler",
  markAllRead: "Merk alle som lest",
  clearAll: "Slett alle",
  accept: "Godta",
  decline: "Avslå",

  // ── Friends ─────────────────────────────────────────────────────────────────
  friends: "Venner",
  findPlayers: "Finn spillere",
  searchByName: "Søk etter navn…",
  requests: "Forespørsler · {n}",
  yourFriends: "Dine venner · {n}",
  addMeOnKickr: "Legg meg til på kickr",
  scanToSendRequest: "Skann for å sende venneforespørsel",
  copyLink: "Kopier lenke",
  add: "Legg til",
  friends_label: "Venner",
  requested: "Forespurt",
  invite: "Inviter",
  noFriendsYet: "Ingen venner ennå — søk etter spillere over!",
  noOpenGames: "Ingen åpne kamper å invitere til ennå.",
  alreadyInGames: "{firstName} er allerede i alle dine åpne kamper.",
  createAGame: "Opprett en kamp",

  // ── Create / Edit game ──────────────────────────────────────────────────────
  createGame: "Opprett kamp",
  createAGameTitle: "Opprett en kamp",
  titleLabel: "Tittel",
  titlePlaceholder: "f.eks. Onsdag 5v5",
  descriptionLabel: "Beskrivelse",
  descriptionPlaceholder: "Detaljer for spillere...",
  dateLabel: "Dato",
  timeLabel: "Tid",
  kickoffTime: "Avsparktid",
  durationLabel: "Varighet — {minutes} min",
  minPlayersLabel: "Minimum spillere for å bekrefte kamp",
  minPlayersHint: "Kampen avlyses automatisk og varsler spillere hvis minimum ikke er nådd 3 timer før avspark",
  priceLabel: "Pris",
  costPerPlayerLabel: "Kostnad per spiller",
  repeatLabel: "Gjenta ukentlig",
  repeatDescription: "Opprett samme kamp for flere uker",
  repeatForHowManyWeeks: "Gjenta for hvor mange uker?",
  weeksLabel: "{n} uker",
  searchVenues: "Søk baner…",
  customLocation: "+ Egendefinert sted",
  address: "Adresse",
  venueLabel: "Bane",
  stepFormat: "Format",
  stepVenue: "Bane",
  stepTime: "Tid",
  stepLabel: "Steg {current} av {total}",
  whatKindOfGame: "Hva slags kamp?",
  whereAndWho: "Hvor og hvem?",
  whenIsIt: "Når er det?",
  creating: "Oppretter...",
  summaryLabel: "Sammendrag",
  formatSummary: "Format",
  timeSummary: "Tid",
  durationSummary: "Varighet",
  costSummary: "Kostnad",
  minPlayersSummary: "Min. spillere for å bekrefte",
  maxPlayersSummary: "Maks spillere",
  paymentCollected: "Betaling skjer på banen. Spillere forventes å betale {price} når de ankommer.",
  gameAlreadyHere: "Kamp allerede her",
  gameAlreadyHereDesc: "Det er allerede {n} kamp{s} på denne banen {date}. Vil du bli med i stedet?",
  createAnyway: "Opprett likevel",
  view: "Vis",
  cloning: "Kloner siste kamp — rediger det du vil nedenfor",
  thisTimePassed: "Dette tidspunktet har allerede passert — velg et fremtidig tidspunkt",
  addressSuggestion: "Velg en adresse fra forslagene",
  repeatWeeksWillCreate: "{n} kamp vil bli opprettet, fra {date}",
  repeatWeeksWillCreatePlural: "{n} kamper vil bli opprettet, fra {date}",
  ageRestriction: "Aldersgrense",
  ageRestrictionHint: "Vis kun til spillere {age}+",
  privateGame: "Privat kamp",
  privateGameHint: "Kun inviterte spillere kan se dette",

  // ── Discover / Filters ──────────────────────────────────────────────────────
  discoverTab: "Oppdage",
  myGamesTab: "Mine kamper",
  mapTab: "Kart",
  profileTab: "Profil",
  communitiesTab: "Sosialt",
  searchPlaceholder: "Søk kamper, verter...",
  filters: "Filter",
  clearFilters: "Fjern filter",
  level: "Nivå",
  dateFilter: "Dato",
  distanceFilter: "Avstand",
  sortBy: "Sorter etter",
  anyDate: "Hvilken som helst dato",
  dateFrom: "Fra",
  dateUntil: "Til",
  dateRangeHint: "Velg datoer eller bruk en snarvei",
  next7Days: "Neste 7 dager",
  tomorrow: "I morgen",
  weekend: "Helg",
  anyDistance: "Hvilken som helst avstand",
  withinKm: "Innen {n} km",
  distanceRadius: "Radius",
  distanceRangeHint: "Dra for å begrense kamper nær deg",
  useLocationForRadius: "Slå på posisjon for å bruke radius",
  availabilityFilter: "Tilgjengelighet",
  showFullyBooked: "Vis fullbookede",
  showFullyBookedHint: "Ta med kamper uten ledige plasser",
  bestMatch: "Beste treff",
  nearestFirst: "Nærmest først",
  soonestFirst: "Snarest først",
  showGames: "Vis {n} kamp",
  showGamesPlural: "Vis {n} kamper",
  playingToday: "Spiller i dag — plasser tilgjengelig",
  createGameHere: "Opprett kamp her",
  createNewGameHere: "+ Opprett en ny kamp her",
  activeGames: "Aktive kamper · {n}",
  signInToJoin: "Logg inn",

  // ── My Games ─────────────────────────────────────────────────────────────────
  searchMyGames: "Søk etter tittel eller sted...",
  upcoming: "Kommende",
  past: "Tidligere",
  saved: "Lagret",
  noUpcomingGames: "Du har ikke blitt med i noen kamper ennå",
  findAGame: "Finn en kamp",
  noGamesMatch: 'Ingen kamper samsvarer med "{q}"',
  clearSearch: "Slett søk",
  noPastGames: "Ingen tidligere kamper ennå",
  noSavedGames: "Ingen lagrede kamper ennå",
  browseGames: "Bla gjennom kamper",
  showingCompleted: "Viser {n} fullført kamp",
  showingCompletedPlural: "Viser {n} fullførte kamper",
  yourGamesHere: "Kampene dine er her",
  yourGamesHereDesc: "Logg inn for å bli med i kamper, følge timeplanen din og koble deg til andre spillere.",

  // ── Profile ──────────────────────────────────────────────────────────────────
  profileTab2: "Profil",
  yourProfile: "Din profil",
  yourProfileDesc: "Logg inn for å bygge profilen din, sette posisjon og spore fotballhistorikken din.",
  age: "Alder",
  bio: "Om meg",
  gamesPlayed: "Kamper",
  hostedGames: "Vertet",
  signOut: "Logg ut",
  editProfile: "Rediger",
  cancelEdit: "Avbryt",
  saveProfile: "Lagre",
  profileSaved: "Profil lagret",
  deleteAccount: "Slett konto",
  deleteAccountConfirm: "Er du sikker? Dette sletter kontoen din og all dataen din permanent.",
  deleting: "Sletter…",
  yesDelete: "Ja, slett",
  reliabilityScore: "Pålitelighet",

  // ── Availability ─────────────────────────────────────────────────────────────
  imAvailable: "Jeg er ledig",
  imAvailableHint: "Fortell spillere i nærheten når du er ledig",
  setAvailability: "Sett tilgjengelighet",
  availabilityDesc: "Sett når du er ledig. Hvis {n} eller flere spillere er tilgjengelige i nærheten av deg samtidig, blir alle varslet og du kan opprette en kamp med ett trykk.",
  from: "Fra",
  to: "Til",
  availabilityTime: "Tid",
  availabilityTimeHint: "Dra begge ender for å sette når du er ledig",
  allDay: "Hele dagen",
  allDayHint: "Du vises som ledig hele den valgte dagen.",
  formatOptional: "Format (valgfritt)",
  levelOptional: "Nivå (valgfritt)",
  leaveBlankAnyFormat: "La stå tomt for å matche alle format.",
  leaveBlankAnyLevel: "La stå tomt for å matche alle nivåer.",
  clear: "Tøm",
  freeToday: "Ledig i dag",
  freePlayers: "{n} spiller også ledig i nærheten",
  freePlayersPlural: "{n} spillere også ledige i nærheten",
  playersFreeNearbyShort: "{n} ledige i nærheten",
  availabilityMomentumHint: "Legg til din for å få i gang en kamp.",
  joinNearbyPlayers: "Bli med",
  playersFreeCta: "{n} spillere ledige — opprett en kamp?",
  edit: "Rediger",

  // Communities
  communitiesTitle: "Grupper",
  communitiesSubtitle: "Bli med i grupper av spillere i byen din",
  yourCommunities: "Dine grupper",
  discoverCommunities: "Oppdag",
  createCommunity: "Opprett gruppe",
  communityName: "Gruppenavn",
  communityDescription: "Beskrivelse",
  communityPrivacy: "Personvern",
  communityPublic: "Offentlig",
  communityPrivate: "Privat",
  communityPublicDesc: "Alle kan finne og bli med",
  communityPrivateDesc: "Kun med invitasjon, skjult fra Oppdag",
  communityJoinPolicy: "Hvem kan bli med",
  joinPolicyOpen: "Alle",
  joinPolicyRequest: "På forespørsel",
  joinPolicyInviteOnly: "Kun invitasjon",
  communityCity: "By",
  communityTags: "Tagger",
  noCommunitiesYet: "Ingen grupper ennå",
  communitiesComingSoon: "Opprett et eller bli med i en eksisterende gruppe for å spille jevnlig med samme folk.",
  noCommunitiesInCity: "Ingen grupper i byen din ennå",
  beFirstToCreate: "Vær den første til å opprette et.",
  joinCommunity: "Bli med",
  leaveCommunity: "Forlat",
  requestToJoin: "Be om å bli med",
  requestPending: "Forespørsel sendt",
  membersCount: "{n} medlemmer",
  memberSingular: "1 medlem",
  feedTab: "Feed",
  gamesTab: "Kamper",
  membersTab: "Medlemmer",
  ownerRole: "Eier",
  adminRole: "Admin",
  modRole: "Mod",
  memberRole: "Medlem",
  communityGamesEmpty: "Ingen kamper i denne gruppen ennå",
  communityCreate: "Opprett",
  creatingCommunity: "Oppretter…",
  communityCreateError: "Kunne ikke opprette gruppe",
  communityJoinError: "Kunne ikke bli med",
  communityLeaveError: "Kunne ikke forlate",
  confirmLeaveCommunity: "Forlat denne gruppen?",
  gameScope: "Omfang",
  scopePublic: "Offentlig",
  scopeCommunity: "Gruppe",
  scopePrivate: "Privat",
  scopePublicDesc: "Åpent for alle",
  scopeCommunityDesc: "Kun synlig for gruppemedlemmer",
  scopePrivateDesc: "Kun med invitasjon",
  pickCommunity: "Velg en gruppe",
  featuredCommunity: "Anbefalt gruppe",
  memberBadge: "Medlem",
  communitiesHeroTitle: "Finn laget ditt i {city}",
  communitiesHeroSubtitle: "Bli med i en gruppe spillere som møtes jevnlig. Nye venner, nye kamper, hver uke.",
  trendingIn: "Populært i {city}",
  moreToExplore: "Mer å utforske",

  // ── Communities Discover (redesigned) ────────────────────────────────────────
  communities: "Grupper",
  myCommunities: "Mine grupper",
  allCommunities: "Alle grupper",
  all: "Alle",
  mine: "Mine",
  discover: "Oppdag",
  searchCommunitiesPlaceholder: "Søk grupper, tagger, steder…",
  trendingToday: "Populært i dag",
  trendingPopular: "Mest populære",
  trendingActive: "Mest aktive",
  trendingNewest: "Nyeste",
  filterLocation: "Sted",
  filterTags: "Tagger",
  filterSort: "Sortér",
  filterLocationTitle: "Filtrer etter sted",
  filterTagsTitle: "Filtrer etter tagg",
  filterSortTitle: "Sortér etter",
  anyLocation: "Hvor som helst",
  anyTag: "Alle tagger",
  sortByPopular: "Mest populære",
  sortByActive: "Mest aktive",
  sortByNewest: "Nyeste",
  noCity: "Ingen plassering",
  viewCommunity: "Se gruppe",
  openCommunity: "Åpne",
  verifiedBadge: "Verifisert",
  communityOpen: "Åpent",
  communityClosed: "Lukket",
  noCommunitiesMatch: "Ingen grupper passer disse filtrene",
  noJoinedCommunities: "Du har ikke blitt med i noen grupper ennå",
  showGroups: "Vis grupper",

  // ── Community Screen (redesigned) ────────────────────────────────────────────
  aboutTab: "Om",
  aboutDescriptionTitle: "Beskrivelse",
  aboutNoDescription: "Denne gruppen har ikke lagt til en beskrivelse ennå.",
  communityTagsTitle: "Gruppetagger",
  verifiedCommunityTitle: "Verifisert gruppe",
  verifiedCommunityDesc: "Denne gruppen er gjennomgått og betrodd av kickr-teamet.",
  communityAdminTitle: "Gruppeadmin",
  statMembersLabel: "Medlemmer",
  statGamesLabel: "Kamper",
  shareCommunity: "Del",
  reportCommunity: "Rapporter",
  reportCommunityTitle: "Rapporter denne gruppen",
  reportCommunityHint: "Fortell oss hva som er galt. En moderator vil se på rapporten din.",
  reportReasonPlaceholder: "Beskriv problemet (spam, misbruk, off-topic…)",
  submitReport: "Send rapport",
  reporting: "Sender…",
  reportSubmitted: "Rapport sendt — takk for at du sa fra.",
  reportError: "Kunne ikke sende rapport",
  youSuffix: "deg",
  editCover: "Rediger forsidebilde",
  editLogo: "Rediger logo",
  uploading: "Laster opp…",
  imageUploadError: "Kunne ikke laste opp bilde",
  imageBucketMissing: "Lagring ikke satt opp — admin må opprette \"community-images\"-bøtte.",
  postPlaceholder: "Del noe med gruppen…",
  post: "Publiser",
  posting: "Publiserer…",
  postEmptyError: "Skriv noe først",
  postError: "Kunne ikke publisere",
  noPostsYet: "Ingen innlegg ennå",
  beTheFirst: "Vær den første til å dele noe med gruppen.",
  joinToPost: "Bli med i gruppen for å poste.",
  pinned: "Festet",
  pinPost: "Fest",
  unpinPost: "Løsne",
  deletePost: "Slett",
  confirmDeletePost: "Slett dette innlegget?",
  edited: "redigert",
  justNow: "akkurat nå",
  minutesAgo: "{n}m siden",
  hoursAgo: "{n}t siden",
  daysAgo: "{n}d siden",
  createGameInCommunity: "Opprett en kamp for denne gruppen",
  shareableGamesTitle: "Del en av kampene dine",
  shareableGamesHint: "Legg til en eksisterende kamp du arrangerer så medlemmer ser den her.",
  shareToCommunity: "Legg til",
  sharingGame: "Legger til…",
  chatTab: "Chat",
  chatPlaceholder: "Skriv til gruppen…",
  chatJoinToWrite: "Bli med i gruppen for å chatte.",
  chatEmpty: "Ingen meldinger ennå. Si hei!",
  chatSendError: "Kunne ikke sende melding",
  challengesTitle: "Utfordringer",
  challengeAnotherCommunity: "Utfordre en annen gruppe",
  pickOpponent: "Velg gruppe å utfordre",
  proposeMatch: "Foreslå kamp",
  sendingChallenge: "Sender…",
  challengeSent: "Utfordring sendt!",
  challengeAccept: "Aksepter",
  challengeDecline: "Avslå",
  challengeCancel: "Avbryt",
  challengePendingIn: "Ventende utfordringer til gruppen din",
  challengePendingOut: "Ventende utfordringer du har sendt",
  challengeAcceptedTitle: "Aksepterte kamper",
  challengeAcceptedHint: "En admin kan nå opprette kampen.",
  noOpponentsAvailable: "Ingen andre grupper tilgjengelig ennå.",
  challengeNoteOptional: "Legg til en melding (valgfritt)",
  challengeError: "Kunne ikke sende utfordring",
  challengeUpdateError: "Kunne ikke oppdatere utfordring",
  vsLabel: "mot",
  challengeNotAdmin: "Bare admins kan utfordre andre grupper.",
  locationLabel: "Sted",

  // ── Referee (optional) ───────────────────────────────────────────────────────
  refereeLabel: "Dommer",
  refereeOptional: "Dommer (valgfritt)",
  refereeHelp: "Velg en venn til å dømme kampen",
  pickReferee: "Velg en dommer",
  searchFriends: "Søk etter venner",
  noFriendsForReferee: "Legg til venner først for å sette dommer",
  noFriendsMatch: "Ingen venner samsvarer",
  clearReferee: "Fjern",
  refereeBadge: "Dom",
  notifRefereeAssignedText: "Du dømmer «{title}»",
  notifRefereeAssignedSub: "{date} · {time}",
  noneLabel: "Ingen",
  createGameFromChallenge: "Opprett kampen",

  // ── Toast messages ───────────────────────────────────────────────────────────
  linkCopied: "Lenke kopiert!",
  couldNotCopyLink: "Kunne ikke kopiere lenke",
  teamsCleared: "Lag tømt",
  failedToClearTeams: "Kunne ikke tømme lag",
  gameUpdated: "Kamp oppdatert!",
  failedToSave: "Kunne ikke lagre — prøv igjen",
  teamsPublished: "Lag publisert!",
  couldNotSaveTeams: "Kunne ikke lagre lag",
  failedToSendMessage: "Kunne ikke sende melding — prøv igjen",
  failedToRemovePlayer: "Kunne ikke fjerne spiller",
  friendRequestSent: "Venneforespørsel sendt!",
  couldNotSendRequest: "Kunne ikke sende forespørsel",
  friendAdded: "Venn lagt til!",
  couldNotAcceptRequest: "Kunne ikke godta forespørsel",
  couldNotDeclineRequest: "Kunne ikke avslå forespørsel",
  couldNotSendInvite: "Kunne ikke sende invitasjon — prøv igjen",
  availabilitySaveError: "Kunne ikke lagre tilgjengelighet. Prøv igjen om litt.",
  backupPitchSyncWarning: "Kampen ble opprettet, men reservebanen kunne ikke synkes for alle ennå.",
  errorCancellingGame: "Feil ved avlysning av kamp",
  youJoined: "Du ble med i kampen!",
  leftGame: "Forlot kampen",
  leftGameWaitlistPromoted: "Forlot kampen. {name} fikk plass.",
  addedToWaitlist: "Lagt til på venteliste — du varsles hvis en plass åpner seg",
  removedFromWaitlist: "Fjernet fra venteliste",
  somethingWentWrong: "Noe gikk galt",
  couldNotGenerateQR: "Kunne ikke generere QR",
  gameCreated: "Kamp opprettet",
  gamesCreatedSeries: "{n} kamper opprettet (ukentlig serie)",
  gameCancelledNotified: "Kamp avlyst — {n} spiller{s} varslet",
  gameCancelled: "Kamp avlyst",
  requestNoLongerAvailable: "Forespørselen er ikke lenger tilgjengelig",
  inviteSent: "Invitasjon sendt for {title}!",
  failedToLoadGames: "Kunne ikke laste kamper",
  reloadApp: "Last appen på nytt",
  somethingWrongErrorBoundary: "Noe gikk galt",
  playerRemovedFromGame: "{name} fjernet fra kampen",
  playerPromotedFromWaitlist: "{name} forfremmet fra venteliste",
  failedToPromotePlayer: "Kunne ikke forfremme spiller",
  mustBeSignedIn: "Du må være innlogget for å opprette en kamp",
  anyFormat: "Hva som helst format",
  memberSince: "Medlem siden",
  joinedStat: "Deltatt",
  noBioYet: "Ingen beskrivelse ennå",
  basedOnGames: "Basert på {n} kamp{s}",
  deleteAccountTitle: "Slette kontoen din?",
  deleteAccountBody: "Dette fjerner profilen din og spillhistorikken permanent. Dette kan ikke angres.",

  // ── Countdown strings ────────────────────────────────────────────────────────
  startingNow: "Starter nå",
  startedMAgo: "Startet for {m}m siden",
  startedHAgo: "Startet for {h}t siden",
  startedDAgo: "Startet for {d}d siden",
  startsInM: "Starter om {m}m",
  startsInHM: "Starter om {h}t {m}m",
  startsInDay: "Starter om {d} dag",
  startsInDays: "Starter om {d} dager",

  // ── Date labels ──────────────────────────────────────────────────────────────
  todayLabel: "I dag",
  tomorrowLabel: "I morgen",

  // ── Venue page (TGTG-style) ──────────────────────────────────────────────────
  overallExperience: "Generell baneopplevelse",
  basedOnReviews: "Basert på {n} anmeldelse{s}",
  noReviewsYet: "Ingen anmeldelser ennå — bli den første",
  surface: "Underlag",
  availability: "Tilgjengelighet",
  lighting: "Belysning",
  directions: "Veibeskrivelse",
  upcomingHere: "Kommende kamper her",
  noUpcomingHere: "Ingen kommende kamper på denne banen",
  nearbyAlternatives: "Alternativer i nærheten",

  // ── Post-game review (simplified) ────────────────────────────────────────────
  gameFinished: "Kampen er ferdig",
  latestGameReady: "{title} er klar for oppsummering.",
  wrapUpLatest: "Oppsummer siste kamp",
  wrapUp: "Oppsummer",
  finishReport: "Fullfør rapport",
  reviewed: "Anmeldt",
  matchReportTitle: "Oppsummer kampen",
  matchReportHint: "Tar bare noen sekunder etter kampen.",
  rateThisGame: "Hvordan var kampen?",
  manOfTheMatch: "Banens beste",
  manOfTheMatchHint: "Velg spilleren som skilte seg ut.",
  optionalNote: "Notat (privat)",
  optionalNotePlaceholder: "Noe du vil huske?",
  attendance: "Oppmøte",
  noShowQuestion: "Hvem møtte ikke?",
  noShowCaution: "Marker bare spillere som skulle være med, men ikke dukket opp.",
  everyoneShowed: "Alle møtte",
  selected: "Valgt",
  markNoShow: "Marker no-show",
  reportedNoShow: "No-show",
  reportNoShows: "Rapporter no-shows",
  noShowsHelp: "Trykk på en spiller som ikke møtte. Holder reliability-score rettferdig.",
  noPlayersToReport: "Ingen andre spillere var registrert på denne kampen.",
  ratePitchOptional: "Vurder banen (valgfritt)",
  ratePitchHelp: "Hjelper neste spiller med å velge.",
  pitchFree: "Ledig",
  pitchTaken: "Opptatt",
  submitReview: "Send inn",
  everythingGood: "Alt var fint",
  reviewThanks: "Takk for tilbakemeldingen",
  alreadyReviewed: "Du har anmeldt denne kampen",
  later: "Senere",

  // ── Profile (refined) ────────────────────────────────────────────────────────
  memberSinceShort: "Medlem siden {d}",
  recentActivity: "Nylig aktivitet",
  seeAllPast: "Se alle tidligere kamper",
  achievements: "Utmerkelser",
  noAchievementsYet: "Spill din første kamp for å låse opp et merke",
  avgRatingReceived: "Snittvurdering",
  avgRatingNone: "Ingen vurderinger ennå",
  badgePunctual: "Punktlig",
  badgePunctualDesc: "Null no-shows på 5+ kamper",
  badgeHost: "Vert",
  badgeHostDesc: "Arrangert 1+ kamp",
  badgeRegular: "Stamspiller",
  badgeRegularDesc: "10+ kamper",
  badgeVeteran: "Veteran",
  badgeVeteranDesc: "50+ kamper",
  badgeFiveStar: "Topprated",
  badgeFiveStarDesc: "Snittvurdering ≥ 4,5",

  // ── Settings screen ──────────────────────────────────────────────────────────
  settings: "Innstillinger",
  account: "Konto",
  preferences: "Preferanser",
  privacy: "Personvern",
  notificationsGameReminders: "Kamppåminnelser",
  notificationsFriendRequests: "Venneforespørsler",
  notificationsChat: "Chatmeldinger",
  language: "Språk",
  theme: "Tema",
  themeLight: "Lys",
  themeDark: "Mørk",
  themeSystem: "System",
  profileVisibility: "Profilsynlighet",
  profileVisibilityPublic: "Offentlig",
  profileVisibilityFriends: "Kun venner",
  about: "Om",
  appVersion: "Appversjon",
  termsOfService: "Vilkår",
  privacyPolicy: "Personvernerklæring",
  greetingMorning: "God morgen",
  greetingAfternoon: "God ettermiddag",
  greetingEvening: "God kveld",
  greetingNight: "Sent ute",
  games: "Kamper",
  venues: "Baner",
  players: "Spillere",
};

const DICT = isNorwegian ? NB : EN;

/**
 * Translate a key, optionally interpolating variables.
 * Usage: t("welcomeBack") or t("resetLinkSentTo", { email: "a@b.com" })
 */
export function t(key, vars) {
  let str = DICT[key] ?? EN[key] ?? key;
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    });
  }
  return str;
}

/** Translate a level name (stored as English in DB, displayed in local language) */
export function tLevel(level) {
  const map = {
    Beginner: t("levelBeginner"),
    Intermediate: t("levelIntermediate"),
    Advanced: t("levelAdvanced"),
    "All levels": t("levelAll"),
  };
  return map[level] ?? level;
}
